import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { GameStatus, GameType, LiveCompany } from '@ottd/api';

import { FleetChips } from '@/components/prototype/fleet-chips';
import { Hint } from '@/components/prototype/hint';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { PresidentFace, companyColorHex } from '@/components/prototype/president-face';
import { ProgressBar } from '@/components/prototype/progress-bar';
import { GrowthChart } from '@/components/prototype/growth-chart';
import { formatInGameMonth, formatMoney, formatPopulation, formatSignedPoints } from '@/lib/format';
import { tr } from '@/lib/locale';
import { cn } from '@/lib/utils';
import { parseGoalNumber } from '@/pages/shared';

/*
 * A live company card (prototype §4, variant E hybrid), player-first: the
 * identity tile leads with the player's avatar and name, and the in-game
 * president portrait shrinks to a badge pinned on the avatar's corner (its
 * background is the company colour, so the pairing still reads at a glance).
 * A company nobody on the site claims falls back to the portrait alone.
 *
 * The header tag tracks the game's lifecycle: "Leader / Chasing" only means
 * something while the game runs; once it ended the winner is starred and the
 * rest carry no tag, and a cancelled game has neither.
 */
function progressPercent(company: LiveCompany, type: GameType, goal: string): number {
  const target = parseGoalNumber(goal);
  if (target === null) return 0;
  const metric = type === 'CB' ? (company.population ?? 0) : company.value;
  return Math.round((metric / target) * 100);
}

/** "★ Leader" / "Chasing" while live; "★ Winner" once ended (gold when leading). */
function RankTag({ tag, isLeader }: { tag: string; isLeader: boolean }) {
  return (
    <span
      className={cn(
        'font-display shrink-0 text-[12px] uppercase',
        isLeader ? 'text-gold' : 'text-dim',
      )}
    >
      {tag}
    </span>
  );
}

function rankTag(company: LiveCompany, status: GameStatus): string | null {
  if (status === 'live') {
    return company.rank === 'LEADER' ? tr('game:company.leader') : tr('game:company.chasing');
  }
  if (status === 'ended') return company.isWinner ? tr('game:company.winner') : null;
  return null;
}

/** Player avatar with the president badge, or the portrait alone for a guest. */
function IdentityPortrait({ company }: { company: LiveCompany }) {
  if (!company.player) {
    return <PresidentFace color={company.color} name={company.name} width={52} />;
  }
  return (
    <span className="relative inline-block shrink-0">
      <PlayerAvatar name={company.player} src={company.avatar} size={64} decorative />
      <PresidentFace
        color={company.color}
        name={company.name}
        width={24}
        strip={false}
        className="absolute -right-2 -bottom-2 shadow-hard-sm"
      />
    </span>
  );
}

interface CompanyCardProps {
  company: LiveCompany;
  type: GameType;
  goal: string;
  status: GameStatus;
}

export function CompanyCard({ company, type, goal, status }: CompanyCardProps) {
  const { t } = useTranslation('game');
  const { t: tc } = useTranslation('common');
  const isWinner = status === 'ended' && company.isWinner;
  // Gold accents: the live leader, or the winner once the game ended.
  const isLeader = isWinner || (status === 'live' && company.rank === 'LEADER');
  const tag = rankTag(company, status);
  const pct = progressPercent(company, type, goal);
  const color = companyColorHex(company.color);
  const metric = type === 'CB' ? formatPopulation(company.population) : formatMoney(company.value);
  const metricLabel =
    type === 'CB' ? t('company.popIn', { town: company.town ?? '-' }) : t('company.companyValue');

  const meta: string[] = [];
  if (company.stations !== null) meta.push(t('company.stations', { count: company.stations }));
  if (company.income !== null) {
    meta.push(t('company.income', { amount: formatMoney(company.income) }));
  }
  if (company.foundedYear !== null) meta.push(t('company.founded', { year: company.foundedYear }));
  if (company.ageYears !== null) meta.push(t('company.age', { count: company.ageYears }));

  return (
    <article
      className={cn(
        'bevel bg-panel flex',
        isWinner ? 'glow-gold outline-gold outline-2' : 'shadow-hard-sm',
      )}
      data-winner={isWinner || undefined}
    >
      {/* Company-colour spine down the card's left edge. */}
      <span className="w-1.5 shrink-0" style={{ background: color }} aria-hidden />
      <div className="min-w-0 flex-1">
        <header className="bg-title flex items-center gap-2 px-3 py-1.5">
          <span
            className="size-3 shrink-0 border border-black"
            style={{ background: color }}
            aria-hidden
          />
          <h3 className="font-display truncate text-[11px] uppercase text-titletext">
            {company.name}
          </h3>
          {status === 'live' && tag ? <RankTag tag={tag} isLeader={isLeader} /> : null}
          {status === 'ended' ? (
            <span className="ml-auto flex shrink-0 items-center gap-2">
              {tag ? <RankTag tag={tag} isLeader={isLeader} /> : null}
              {company.points !== null ? (
                <Hint
                  label={
                    company.player
                      ? t('company.pointsHint', { who: company.player })
                      : t('company.pointsHintGeneric')
                  }
                >
                  <span
                    className={cn(
                      'bevel-in bg-well font-display shrink-0 px-2 py-0.5 text-[12px]',
                      isWinner ? 'text-gold' : company.points > 0 ? 'text-green' : 'text-dim',
                    )}
                  >
                    {tc('units.points', { points: formatSignedPoints(company.points) })}
                  </span>
                </Hint>
              ) : null}
            </span>
          ) : null}
        </header>

        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-stretch">
          <div className="bevel-in bg-well flex shrink-0 items-center gap-4 px-3.5 py-2.5 sm:min-w-[260px]">
            <IdentityPortrait company={company} />
            <div className="min-w-0">
              {company.player ? (
                <Link
                  to={`/player/${encodeURIComponent(company.player)}`}
                  className="font-display text-strong hover:text-gold block truncate text-base"
                >
                  {company.player}
                </Link>
              ) : (
                <span className="font-display text-dim block text-base">{t('company.guest')}</span>
              )}
              <div className="text-faint text-[10px] uppercase">
                {t('company.runs')} <span style={{ color }}>{company.name}</span>
              </div>
              <Hint label={metricLabel}>
                <span className="font-display text-strong mt-1.5 block text-lg">
                  {metric}
                  {type === 'CB' ? (
                    <span className="text-dim ml-1 text-[11px]">{tc('units.population')}</span>
                  ) : null}
                </span>
              </Hint>
              <div className={cn('font-mono text-[11px]', isLeader ? 'text-gold' : 'text-green')}>
                {tc('units.percentOfGoal', { percent: pct })}
              </div>
            </div>
          </div>

          {/* The company's own read-out: fleet, progress, facts, then the
              curve those facts produced - one column, coarse to fine. */}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2.5">
            <FleetChips fleet={company.fleet} variant="inline" />
            <ProgressBar
              value={pct}
              height={8}
              label={t('company.progressLabel', { company: company.name })}
            />
            {meta.length > 0 ? (
              <p className="text-faint font-mono text-[11px]">{meta.join(' · ')}</p>
            ) : null}
            {company.growthSeries.length > 0 ? (
              <GrowthChart
                points={company.growthSeries.map((sample) => ({
                  label: formatInGameMonth(sample.month),
                  value: sample.value,
                }))}
                goal={parseGoalNumber(goal)}
                color={color}
                formatValue={
                  type === 'CB'
                    ? (value) => t('company.popValue', { value: formatPopulation(value) })
                    : formatMoney
                }
              />
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}
